/**
 * Quote estimator math and eligibility rules.
 *
 * Every number comes from the config passed to createEstimator — if a rate, threshold or
 * bucket midpoint appears as a literal in this file, that is a bug.
 */

const MONTHS_PER_YEAR = 12;

const MS_PER_DAY = 86400000;

/** Remove this and $1,250,000 at age 77 understates by $5: 9455.00 is 9454.999999999998 in binary. */
const FLOOR_EPSILON = 1e-9;

export const FEED_CURRENT = 'current';
export const FEED_WARN = 'warn';
export const FEED_HARD_STOP = 'hard_stop';

export const GOAL_INCOME_NOW = 'SPIA';
export const GOAL_INCOME_LATER = 'DIA';
export const GOAL_ACCUMULATION = 'MYGA';

export const SPECIALIST_REASON_AGE = 'age';
export const SPECIALIST_REASON_AMOUNT = 'amount';
export const SPECIALIST_REASON_STALE = 'stale';
export const SPECIALIST_REASON_START_AGE = 'start_age';

/**
 * Local midnight on the config's as-of date.
 *
 * The bare `T00:00:00` with no `Z` is load-bearing — it parses as local time, which is what
 * makes the subtraction against a local `new Date()` come out in whole local days. Adding a
 * `Z`, or switching to `Date.parse(asOfDate)`, shifts every staleness boundary by the UTC
 * offset.
 *
 * @param {string} asOfDate ISO date, YYYY-MM-DD.
 * @returns {Date}
 */
function parseAsOfDate(asOfDate) {
  return new Date(`${asOfDate}T00:00:00`);
}

/**
 * @param {number} asOfMs Epoch ms from parseAsOfDate.
 * @param {Date} now
 * @returns {number} Whole days elapsed.
 */
function daysSince(asOfMs, now) {
  return Math.floor((now.getTime() - asOfMs) / MS_PER_DAY);
}

/**
 * The staleness verdict for a feed of a given age.
 *
 * @param {number} asOfMs
 * @param {{warn_days: number, hard_stop_days: number}} staleness
 * @param {Date} now
 * @returns {string} One of FEED_CURRENT, FEED_WARN, FEED_HARD_STOP.
 */
function feedStatusFor(asOfMs, staleness, now) {
  const ageDays = daysSince(asOfMs, now);

  // An unparseable as_of_date makes ageDays NaN, and every NaN comparison below is false —
  // which would report an unknown-age feed as current and quote from it. Unknown is stale.
  if (Number.isNaN(ageDays)) {
    return FEED_HARD_STOP;
  }

  if (ageDays > staleness.hard_stop_days) {
    return FEED_HARD_STOP;
  }
  if (ageDays > staleness.warn_days) {
    return FEED_WARN;
  }
  return FEED_CURRENT;
}

/**
 * Rounds down to a multiple of step.
 *
 * Down is a compliance requirement for a figure we may be held to, not a preference.
 *
 * @param {number} value
 * @param {number} step
 * @returns {number}
 */
function floorTo(value, step) {
  return Math.floor(value / step + FLOOR_EPSILON) * step;
}

/**
 * Builds an estimator bound to one rate config.
 *
 * @param {Object} config Parsed estimator-config.json.
 * @returns {{config: Object, bucketById: Function, estimate: Function}}
 * @throws {Error} If the config is absent or asks for a rounding direction we do not implement.
 */
export function createEstimator(config) {
  if (!config) {
    throw new Error('createEstimator requires a rate config');
  }

  if (config.rounding.direction !== 'down') {
    throw new Error(`Unsupported rounding direction: ${config.rounding.direction}`);
  }

  const { income, myga, guardrails, staleness, rounding } = config;
  const anchorAges = Object.keys(income.anchors)
    .map(Number)
    .sort((left, right) => left - right);
  const youngestAnchor = anchorAges[0];
  const oldestAnchor = anchorAges[anchorAges.length - 1];
  const asOfMs = parseAsOfDate(config.as_of_date).getTime();

  /**
   * Annual payout rate band at a given age, interpolated between the two bracketing anchors
   * and clamped outside them.
   *
   * Interpolation exists only to remove cliffs — without it a 64- and a 65-year-old would
   * see very different numbers for no defensible reason.
   *
   * @param {number} age
   * @returns {{low: number, high: number}} Annual rates, as decimals.
   */
  function payoutRate(age) {
    if (age <= youngestAnchor) {
      return { ...income.anchors[youngestAnchor] };
    }
    if (age >= oldestAnchor) {
      return { ...income.anchors[oldestAnchor] };
    }

    const upperIndex = anchorAges.findIndex((anchorAge) => anchorAge > age);
    const lowerAge = anchorAges[upperIndex - 1];
    const upperAge = anchorAges[upperIndex];
    const progress = (age - lowerAge) / (upperAge - lowerAge);
    const lower = income.anchors[lowerAge];
    const upper = income.anchors[upperAge];

    return {
      low: lower.low + (upper.low - lower.low) * progress,
      high: lower.high + (upper.high - lower.high) * progress,
    };
  }

  /**
   * Monthly income for a premium paid now, payments starting immediately.
   *
   * @param {number} premium
   * @param {number} age
   * @returns {{low: number, high: number, rate: {low: number, high: number},
   *   rateClamped: boolean}}
   */
  function calcIncomeNow(premium, age) {
    const rate = payoutRate(age);
    const factor = income.cash_refund_factor;

    return {
      low: floorTo((premium * rate.low * factor) / MONTHS_PER_YEAR, rounding.monthly_step),
      high: floorTo((premium * rate.high * factor) / MONTHS_PER_YEAR, rounding.monthly_step),
      rate,
      rateClamped: age > guardrails.max_rate_age,
    };
  }

  /**
   * Monthly income for a premium paid now with payments deferred to a later age.
   *
   * The payout rate is looked up at the age income begins, not the current age, then credited
   * for the wait. Deferral is capped because small errors in the credit assumption compound
   * into large ones over long waits, and real products rarely price that far out.
   *
   * @param {number} premium
   * @param {number} currentAge
   * @param {number} startAge
   * @returns {{low: number, high: number, rate: {low: number, high: number},
   *   deferralYears: number, deferralFactor: number, rateClamped: boolean,
   *   deferralCapped: boolean}}
   */
  function calcIncomeLater(premium, currentAge, startAge) {
    const requestedYears = startAge - currentAge;
    const deferralYears = Math.max(0, Math.min(requestedYears, income.max_deferral_years));
    const deferralFactor = Math.pow(1 + income.deferral_credit_annual, deferralYears);
    const rate = payoutRate(startAge);
    const factor = income.cash_refund_factor;

    return {
      low: floorTo(
        (premium * rate.low * factor * deferralFactor) / MONTHS_PER_YEAR,
        rounding.monthly_step
      ),
      high: floorTo(
        (premium * rate.high * factor * deferralFactor) / MONTHS_PER_YEAR,
        rounding.monthly_step
      ),
      rate,
      deferralYears,
      deferralFactor,
      rateClamped: startAge > guardrails.max_rate_age,
      deferralCapped: requestedYears > income.max_deferral_years,
    };
  }

  /**
   * End-of-term values for every configured term. Assumes the premium is held to term with
   * no withdrawals — surrender charges and market value adjustments are deliberately not
   * modeled, and the disclosures say so.
   *
   * @param {number} premium
   * @returns {Array<{term: number, low: number, high: number,
   *   rate: {low: number, high: number}, emphasis: boolean}>}
   */
  function calcAccumulation(premium) {
    return myga.terms.map((term) => {
      // Copied so a consumer holding the result cannot write back into the config.
      const rate = { ...myga.bands[term] };

      return {
        term,
        low: floorTo(premium * Math.pow(1 + rate.low, term), rounding.value_step),
        high: floorTo(premium * Math.pow(1 + rate.high, term), rounding.value_step),
        rate,
        emphasis: term === myga.emphasis_term,
      };
    });
  }

  /**
   * Whole days between the config's as-of date and now.
   *
   * @param {Date} [now] Injected so callers are not tied to the wall clock.
   * @returns {number}
   */
  function feedAgeDays(now = new Date()) {
    return daysSince(asOfMs, now);
  }

  /**
   * @param {Date} [now]
   * @returns {string} One of FEED_CURRENT, FEED_WARN, FEED_HARD_STOP.
   */
  function feedStatus(now = new Date()) {
    return feedStatusFor(asOfMs, staleness, now);
  }

  /**
   * @param {string} bucketId
   * @returns {?Object} The matching amount bucket, or null.
   */
  function bucketById(bucketId) {
    return config.amount_buckets.find((bucket) => bucket.id === bucketId) || null;
  }

  /**
   * Decides whether this answer set gets a number or the specialist screen. Every branch
   * withholds the number only — the lead is still captured on all of them.
   *
   * @param {{goal: string, bucketId: ?string, age: ?number, forcedByAgeGate: boolean}} answers
   * @param {Date} [now]
   * @returns {?string} A SPECIALIST_REASON_* value, or null when numbers may be shown.
   */
  function routeToSpecialist(answers, now = new Date()) {
    if (feedStatus(now) === FEED_HARD_STOP) {
      return SPECIALIST_REASON_STALE;
    }

    const isIncomePath = answers.goal !== GOAL_ACCUMULATION;

    if (answers.forcedByAgeGate) {
      return SPECIALIST_REASON_AGE;
    }

    if (isIncomePath && answers.age !== null && answers.age < guardrails.min_age_income) {
      return SPECIALIST_REASON_AGE;
    }

    if (
      answers.goal === GOAL_INCOME_LATER &&
      answers.age !== null &&
      answers.age >= guardrails.start_age_max
    ) {
      return SPECIALIST_REASON_START_AGE;
    }

    const bucket = answers.bucketId ? bucketById(answers.bucketId) : null;
    if (isIncomePath && bucket && bucket.income_specialist) {
      return SPECIALIST_REASON_AMOUNT;
    }

    return null;
  }

  /**
   * Computes the estimate for a completed answer set, or the reason no number may be shown.
   *
   * The single entry point callers should use: it decides the path, applies the guardrails,
   * and returns one object carrying everything the results screen and the lead payload need,
   * so both are built from the same computation.
   *
   * @param {{goal: string, bucketId: string, age: number, startAge: ?number,
   *   forcedByAgeGate: boolean}} answers
   * @param {Date} [now]
   * @returns {{showsNumbers: boolean, specialistReason: ?string, goal: string,
   *   premiumBasis: ?number, estimate: ?Object, feedStatus: string,
   *   feedAgeDays: number, asOfDate: string, configVersion: string}}
   */
  function estimate(answers, now = new Date()) {
    const specialistReason = routeToSpecialist(answers, now);
    const shared = {
      goal: answers.goal,
      feedStatus: feedStatus(now),
      feedAgeDays: feedAgeDays(now),
      asOfDate: config.as_of_date,
      configVersion: config.config_version,
    };

    if (specialistReason) {
      return {
        ...shared,
        showsNumbers: false,
        specialistReason,
        premiumBasis: null,
        estimate: null,
      };
    }

    const bucket = bucketById(answers.bucketId);
    const premium = bucket.midpoint;
    const base = { ...shared, showsNumbers: true, specialistReason: null, premiumBasis: premium };

    if (answers.goal === GOAL_INCOME_NOW) {
      return {
        ...base,
        estimate: calcIncomeNow(premium, answers.age),
      };
    }

    if (answers.goal === GOAL_INCOME_LATER) {
      return {
        ...base,
        estimate: calcIncomeLater(premium, answers.age, answers.startAge),
      };
    }

    return {
      ...base,
      estimate: { terms: calcAccumulation(premium) },
    };
  }

  return {
    config,
    bucketById,
    estimate,
  };
}
